import Map, { Marker } from 'react-map-gl';
import { extractLocations, generateStreetviewUrl, mapboxToken } from '@/components/maps/mapbox';
import BaseEventInfo from '@/app/Alert/components/BaseEventInfo';
import { MonitorAlertFullDto } from '@/app/Alert/types';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Link } from 'react-router-dom';
import { buttonVariants } from '../ui/button';
import { Binoculars, CctvIcon } from 'lucide-react';

export type StandaloneMapProps = {
  lat: number;
  lng: number;
  zoom: number;
  alertData: MonitorAlertFullDto;
};

export default function StandaloneMap({
  lat,
  lng,
  zoom,
  alertData,
}: StandaloneMapProps) {
  const draftMode = import.meta.env.DEV ? '/draft' : '';

  const { startLocation, siteLocation } = extractLocations(alertData);

  return (
    <div className="flex h-screen flex-row">
      <div className="w-60 border-r bg-card2">
        <h1 className="border-b p-4 font-semibold text-white">Alert: {alertData.alertId}</h1>
        <BaseEventInfo alertData={alertData} />
        <div className="border-[#2A3B35] p-4 pt-8 text-white">
          <h3 className="mb-2 text-xs font-semibold">Open in streetview</h3>
          <p>
            <Link
              target="_blank"
              referrerPolicy="no-referrer"
              to={
                siteLocation && siteLocation.latitude && siteLocation.longitude
                  ? generateStreetviewUrl(siteLocation)
                  : '#'
              }
              className={buttonVariants({
                variant: 'ghost',
                className: 'w-full bg-[#111] px-2 text-lime shadow-md',
              })}
            >
              Open in streetview
              <Binoculars />
            </Link>
          </p>
        </div>
        <div className="border-[#2A3B35] p-4 text-white">
          <h3 className="mb-2 text-xs font-semibold">Site Notes</h3>
          <p className="text-xs text-white">
            {alertData.project?.siteNotes || 'No site notes available'}
          </p>
        </div>
      </div>
      <div className="flex-1">
        <Map
          mapboxAccessToken={mapboxToken}
          initialViewState={{
            latitude: lat,
            longitude: lng,
            zoom: zoom,
          }}
          style={{ height: '100%', width: '100%' }}
          mapStyle={`mapbox://styles/watchful2024map/cm4by2a1h00pq01rc8q54abca${draftMode}`}
        >
          <Marker longitude={lng} latitude={lat} anchor="bottom">
            <img src="/icon/map-pin.svg" />
          </Marker>
          {alertData.project?.assets?.map((asset) => (
            <Marker
              longitude={asset.address?.lon ?? 0}
              latitude={asset.address?.lat ?? 0}
              anchor="bottom"
            >
              <div className="relative flex flex-row items-center gap-1 rounded-md border border-[#D1F551] p-1 after:absolute after:-bottom-4 after:left-1/2 after:-translate-x-1/2 after:border-8 after:border-transparent after:border-t-[#D1F551]">
                <CctvIcon className="text-[#D1F551]" />
                <p className="text-xs font-medium text-white [text-shadow:_-1px_-1px_0_#000,_1px_-1px_0_#000,_-1px_1px_0_#000,_1px_1px_0_#000]">
                  {asset.serialNumber}
                </p>
              </div>
            </Marker>
          ))}
        </Map>
      </div>
    </div>
  );
}
